import { computed, Ref, ref } from "vue";
import { useStore } from "vuex";
import { LocationQueryRaw, RouteParamsRaw, useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import {
  LESSON_ACTIVITY_COMPONENT_TYPE,
  NAMESPACE,
  QUIZ_PAGE,
} from "@modules/editor/service/editor.constant";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant.ts";
import { isNull, isDefined, notDefined } from "@utils/var.check";
import AssignmentUtils from "@modules/assignment/service/assignment.utils";
import StringUtils from "@utils/string.utils";
import quizNavigatorStore from "@plugins/quiz-navigator/components/QuizNavigatorStore";
import ActivityComponentStore from "@modules/editor/components/activity/ActivityComponentStore";
import DeviceUtils from "@utils/device.utils";
import { ROUTES_VIEW } from "@/modules/attempt/service/attempt.constant";

interface RouteQuery {
  [key: string]: string | string[] | undefined;

  pageId?: string;
  isNavigateToFirstActivity?: string;
  openLessonSettings?: string;
  assignmentUuid?: string;
  version?: string;
  openGameSubpage?: string;
  openAnnotation?: string;
  showAuthoringCoPilot?: string;
}

interface AssignmentRouteParams {
  assignmentUuid: string;
  moduleUuid: string;
  sectionId?: string;
  pageId?: string;
}

interface EditorRouteParams {
  mode: string;
  sectionId?: string;
}

interface UseEditorNavigationOptions {
  isEditorInEditMode?: Ref<boolean>;
  currentPageNo?: Ref<number | string>;
  hasPagination?: Ref<boolean>;
  totalNumActivities?: Ref<number>;
  studentGroupCollectionUuid?: Ref<string | null>;
}

export function useEditorNavigation({
  isEditorInEditMode = ref(false),
  currentPageNo = ref(0),
  hasPagination = ref(false),
  totalNumActivities = ref(0),
  studentGroupCollectionUuid = ref<string | null>(null),
}: UseEditorNavigationOptions = {}) {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const modal = useModal();

  // Data
  const quizNavigatorStoreRef = ref(quizNavigatorStore);
  const activityComponentStoreRef = ref(ActivityComponentStore);

  // Computed - mapState
  const lesson = computed(() => store.getters[`${NAMESPACE.LESSON}/getLesson`]);

  // Computed - mapGetters
  const isCoursePackageType = computed(
    () => store.getters[`${NAMESPACE.LESSON}/isCoursePackageType`],
  );
  const currentPageId = computed(() => store.getters[`${NAMESPACE.LESSON}/currentPageId`]);
  const currentSectionId = computed(() => store.getters[`${NAMESPACE.LESSON}/currentSectionId`]);
  const currentSection = computed(() => store.getters[`${NAMESPACE.LESSON}/currentSection`]);
  const hideSectionActivityNumber = computed(
    () => store.getters[`${NAMESPACE.LESSON}/hideSectionActivityNumber`],
  );
  const getActivity = computed(() => store.getters[`${NAMESPACE.LESSON}/getActivity`]);
  const getActivityList = computed(() => store.getters[`${NAMESPACE.LESSON}/getActivityList`]);
  const resourceDomain = computed(() => store.getters[`${NAMESPACE.LESSON}/resourceDomain`]);
  const isAdminPage = computed(() => store.getters[`${NAMESPACE.LESSON}/isAdminPage`]);
  const targetLessonStatus = computed(
    () => store.getters[`${NAMESPACE.LESSON}/targetLessonStatus`],
  );
  const getSectionList = computed(() => store.getters[`${NAMESPACE.LESSON}/getSectionList`]);
  const getLeafComponentsInActivity = computed(
    () => store.getters[`${NAMESPACE.LESSON}/getLeafComponentsInActivity`],
  );
  const showSectionCover = computed(() => store.getters[`${NAMESPACE.LESSON}/showSectionCover`]);
  const getComponentsByContainerId = computed(
    () => store.getters[`${NAMESPACE.LESSON}/getComponentsByContainerId`],
  );
  const currentMode = computed(() => store.getters[`${NAMESPACE.EDITOR}/currentMode`]);
  const isSectionActivityNumberHidden = computed(
    () => !isEditorInEditMode.value && hideSectionActivityNumber.value,
  );
  const activityList = computed(() => getActivityList.value(currentSectionId.value));
  const sectionData = computed(() => (currentSection.value ? currentSection.value : {}));
  const nextSectionData = computed(() => {
    if (isLastSection.value) {
      return {};
    }
    const sectionList = getSectionList.value;
    const nextSection = sectionList.find(
      (section) => section.pageNo === currentSection.value.pageNo + 1,
    );
    return nextSection || {};
  });
  const fromCover = computed(() => currentPageId.value === "cover");
  const fromSectionCover = computed(() => isActiveSectionCoverPage.value(currentSectionId.value));
  const fromActivity = computed(() => !!getActivity.value(currentPageId.value));

  const fromModulePlan = computed(() => currentPageId.value === "modulePlan");
  const fromModulePlanReorder = computed(() => currentPageId.value === "modulePlanReorder");

  const isActiveSectionCoverPage = computed(() => (sectionId) => {
    return currentPageId.value === "sectionCover" && currentSectionId.value === sectionId;
  });
  const isActiveActivityPage = computed(() => (activityId) => {
    return currentPageId.value === activityId.toString();
  });
  const isFirstSection = computed(() => sectionData.value.pageNo === 1);
  const isLastSection = computed(() => sectionData.value.pageNo === getSectionList.value.length);
  const sectionLabel = computed(() =>
    currentSection.value ? StringUtils.getAlphabeticalLetter(currentSection.value.pageNo - 1) : "",
  );
  const nextSectionLabel = computed(() =>
    currentSection.value ? StringUtils.getAlphabeticalLetter(currentSection.value.pageNo) : "",
  );
  const hasSections = computed(() => getSectionList.value.length > 0);
  const hasActivities = computed(() => {
    let hasActivitiesValue = false;
    getSectionList.value.every((section) => {
      if (getActivityList.value(section.id).length > 0) {
        hasActivitiesValue = true;
        return false;
      }
      return true;
    });
    return hasActivitiesValue;
  });
  const activitiesInFirstSection = computed(() => {
    const firstSection = getSectionList.value.find((section) => {
      return section.pageNo === 1;
    });
    return getActivityList.value(firstSection.id);
  });
  const previousNavigationSectionLabel = computed(() =>
    isFirstSection.value
      ? t("course_plan_introduction_label")
      : currentSection.value
        ? previousSectionLastActivityLabel.value
        : "",
  );
  const nextNavigationSectionLabel = computed(() => {
    if (activityList.value.length > 0) {
      const firstActivity = activityList.value[0];

      if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        return getBottomNavigationActivityLabel(firstActivity.pageNo, firstActivity.title);
      } else if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
        if (isActivityHasPagination(firstActivity.id)) {
          return getBottomNavigationActivityPageLabel(firstActivity.pageNo, firstActivity.title, 1);
        }
        return getBottomNavigationActivityLabel(firstActivity.pageNo, firstActivity.title);
      }
    } else {
      const nextPageIndex = sectionData.value.pageNo;
      const nextSection = getSectionList.value[nextPageIndex];
      if (nextPageIndex < getSectionList.value.length) {
        return getBottomNavigationSectionDefaultLabel(nextSection.pageNo - 1, nextSection.title);
      }
    }
    return "";
  });
  const previousSectionLastActivityLabel = computed(() => {
    if (isFirstSection.value) {
      return "";
    } else {
      const previousPageIndex = sectionData.value.pageNo - 2;
      const previousSection = getSectionList.value[previousPageIndex];
      const activityListValue = getActivityList.value(previousSection.id);

      if (activityListValue.length > 0) {
        const lastActivity = activityListValue[activityListValue.length - 1];
        const activityNumber = lastActivity.pageNo;

        if (lastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          return getBottomNavigationActivityQuizEndLabel(activityNumber);
        } else if (lastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          const lastActivityLastPageNo = isActivityHasPagination(lastActivity.id)
            ? getActivityPageNo(lastActivity.id)
            : null;

          if (isActivityHasPagination(lastActivity.id)) {
            return getBottomNavigationActivityPageLabel(
              activityNumber,
              lastActivity.title,
              lastActivityLastPageNo,
            );
          } else {
            return getBottomNavigationActivityLabel(activityNumber, lastActivity.title);
          }
        }
      }

      return getBottomNavigationSectionDefaultLabel(
        previousSection.pageNo - 1,
        previousSection.title,
      );
    }
  });

  // Methods
  const getActivityPageNo = (pageId) => {
    return getComponentsByContainerId.value(0, pageId.toString())
      ? getComponentsByContainerId.value(0, pageId.toString()).length
      : 0;
  };

  const isActivityHasPagination = (pageId) => {
    const containers = getComponentsByContainerId.value(0, pageId.toString());
    return containers.length > 1;
  };

  const processRouteQuery = (params): RouteQuery => {
    const routeQuery: RouteQuery = {};

    // mandatory route params:
    // register if given, otherwise ignore (use existing)
    if (params.isNavigateToFirstActivity)
      routeQuery.isNavigateToFirstActivity = params.isNavigateToFirstActivity;
    if (params.openLessonSettings) routeQuery.openLessonSettings = params.openLessonSettings;
    if (route?.query?.assignmentUuid ?? false) {
      routeQuery.assignmentUuid = String(route.query.assignmentUuid);
    }
    if (route?.query?.version ?? false) {
      routeQuery.version = String(route.query.version);
    }
    if (route?.query?.openGameSubpage) {
      routeQuery.openGameSubpage = String(route.query.openGameSubpage);
    }
    if (params.openAnnotation) {
      routeQuery.openAnnotation = String(params.openAnnotation);
    } else if (route?.query?.openAnnotation) {
      routeQuery.openAnnotation = String(route.query.openAnnotation);
    }
    if (params.showAuthoringCoPilot) {
      routeQuery.showAuthoringCoPilot = String(params.showAuthoringCoPilot);
    }
    return routeQuery;
  };

  const processRouteParams = (params: Partial<RouteParamsRaw>): RouteParamsRaw => {
    const routeParams: RouteParamsRaw = {};

    // mandatory route params:
    // register if given, otherwise ignore (use existing)
    if (params.resourceDomainParam) routeParams.resourceDomain = params.resourceDomainParam;
    if (params.mode) routeParams.mode = params.mode;
    if (params.lessonUuid) routeParams.lessonUuid = params.lessonUuid;
    if (params.pageId) routeParams.pageId = params.pageId;
    if (params.sectionId) routeParams.sectionId = params.sectionId;
    if (params.pageNo) routeParams.pageNo = params.pageNo;
    if (route.params.fromLocation) routeParams.fromLocation = String(route.params.fromLocation);
    if (params.openGameSubpage) routeParams.openGameSubpage = params.openGameSubpage;
    if (params.showAuthoringCoPilot) routeParams.showAuthoringCoPilot = params.showAuthoringCoPilot;

    // optional route params:
    // register if given, otherwise check the current value
    if (
      (isDefined(params.isAdminPageParam) && params.isAdminPageParam) ||
      (notDefined(params.isAdminPageParam) && isAdminPage.value)
    ) {
      routeParams.admin = "admin";
    }

    if (isDefined(params.status)) {
      routeParams.status = String(params.status).toLowerCase();
    } else if (notDefined(params.status) && targetLessonStatus.value) {
      routeParams.status = String(targetLessonStatus.value).toLowerCase();
    }

    return routeParams;
  };

  const getEditorPageRoute = (paramsValue) => ({
    name: "EditorActivityPage",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const routeToEditorPage = (paramsValue) =>
    router.push({
      name: "EditorActivityPage",
      params: processRouteParams(paramsValue),
      query: processRouteQuery(paramsValue),
    });

  const getEditorCoverRoute = (paramsValue) => ({
    name: "EditorCover",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const routeToEditorCover = (paramsValue) =>
    router.push({
      name: "EditorCover",
      params: processRouteParams(paramsValue),
      query: processRouteQuery(paramsValue),
    });

  const getEditorEndRoute = (paramsValue) => ({
    name: "EditorEnd",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const getEditorModulePlanRoute = (paramsValue) => ({
    name: "EditorModulePlan",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const getEditorModulePlanReorderRoute = (paramsValue) => ({
    name: "EditorModulePlanReorder",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const getEditorModulePlanLearningAssistantSettingsRoute = (paramsValue) => ({
    name: "EditorModulePlanLearningAssistantSettings",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const getEditorSectionCoverRoute = (paramsValue) => ({
    name: "EditorSectionCover",
    params: processRouteParams(paramsValue),
    query: processRouteQuery(paramsValue),
  });

  const routeToEditorSectionCover = (paramsValue) =>
    router.push({
      name: "EditorSectionCover",
      params: processRouteParams(paramsValue),
      query: processRouteQuery(paramsValue),
    });

  const routeToNotFoundPage = () => {
    router.push({ name: "EditorNotFoundPage" });
  };

  const getAssignmentViewRouteByCurrentPageId = () => {
    const { eAssessment, lessonAssignmentUuid, uuid } = lesson.value;
    const routes = eAssessment ? ROUTES_VIEW.ASSESSMENT : ROUTES_VIEW.ASSIGNMENT;
    const pageId = currentPageId.value;
    const params: AssignmentRouteParams = {
      assignmentUuid: lessonAssignmentUuid,
      moduleUuid: uuid,
    };

    let route = routes.COVER_PAGE;

    if (pageId === "sectionCover") {
      route = routes.SECTION_COVER_PAGE;
      params.sectionId = currentSectionId.value;
    } else if (pageId === "cover") {
      route = routes.COVER_PAGE;
    } else if (pageId === "modulePlan") {
      route = routes.MODULE_PLAN;
    } else if (pageId) {
      route = routes.ACTIVITY_PAGE;
      params.sectionId = currentSectionId.value;
      params.pageId = pageId;
    }

    return { name: route, params };
  };

  const getRouteByCurrentPageId = (switchMode) => {
    let route = {};
    const pageId = currentPageId.value;
    const routeParams: EditorRouteParams = {
      mode: switchMode ? switchMode : currentMode.value,
    };

    if (pageId === "sectionCover") {
      route = getEditorSectionCoverRoute(routeParams);
    } else if (pageId === "cover") {
      route = getEditorCoverRoute(routeParams);
    } else if (pageId === "end") {
      route = getEditorEndRoute(routeParams);
    } else if (pageId === "modulePlan") {
      route = getEditorModulePlanRoute(routeParams);
    } else if (pageId === "modulePlanReorder") {
      route =
        routeParams.mode === "view"
          ? getEditorModulePlanRoute(routeParams)
          : getEditorModulePlanReorderRoute(routeParams);
    } else if (pageId !== "") {
      const activity = getActivity.value(pageId);
      const sectionId = activity ? `${activity.parentId}` : String(route.params.sectionId);
      routeParams.sectionId = sectionId;
      route = getEditorPageRoute(routeParams);
    } else {
      route = getEditorCoverRoute(routeParams);
    }
    return route;
  };

  const handlePrevious = (currentPage) => {
    if (isNull(currentPage)) {
      // completion page
      if (navigateLessonEndPrevious()) return;
    }
    if (AssignmentUtils.isActivityOrQuizActivity(currentPage.type)) {
      if (navigatePreviousActivity(currentPage)) return;
    }
    if (currentPage.type === LESSON_ACTIVITY_TYPE.SECTION) {
      if (navigatePreviousSection()) return;
    }
    return router.push(getEditorCoverRoute({}));
  };

  const handleNext = (currentPage) => {
    if (isNull(currentPage)) {
      // introduction page
      if (navigateLessonCoverNext()) return;
    }
    if (AssignmentUtils.isActivityOrQuizActivity(currentPage.type)) {
      if (navigateNextActivity(currentPage)) return;
    }
    if (currentPage.type === LESSON_ACTIVITY_TYPE.SECTION) {
      if (navigateNextSection()) return;
    }
    return router.push(getEditorEndRoute({}));
  };

  const navigateLessonCoverNext = () => {
    const nextPageIndex = 0;
    if (isCoursePackageType.value) {
      // if section cover hidden, route to first activity
      if (!showSectionCover.value) {
        const page = activitiesInFirstSection.value[0];
        return goToByPageId(page.id);
      }

      // go to first section cover in course
      const section = getSectionList.value[nextPageIndex];
      return router.push(getEditorSectionCoverRoute({ sectionId: section.id }));
    }
    // go to first activity in lesson
    if (nextPageIndex < activityList.value.length) {
      return goToByPageId(activityList.value[nextPageIndex].id);
    }
    return;
  };

  const navigateLessonEndPrevious = () => {
    const previousPageIndex = activityList.value.length - 1;
    if (previousPageIndex >= 0) {
      return goToByPageId(activityList.value[previousPageIndex].id);
    }
    if (activityList.value.length === 0 && sectionData.value) {
      // go to last section cover for section with no activity
      return router.push(getEditorSectionCoverRoute({ sectionId: sectionData.value.id }));
    }
    return;
  };

  const navigatePreviousActivity = (currentPage) => {
    if (currentPage.pageNo >= 2) {
      //go to previous activity
      const previousPageIndex = currentPage.pageNo - 2;
      if (previousPageIndex >= 0) {
        return goToByPageId(activityList.value[previousPageIndex].id);
      }
    } else if (currentPage.parentId !== null) {
      //go to section cover
      const section = getSectionList.value.find((section) => {
        return section.id === currentPage.parentId;
      });
      return router.push(getEditorSectionCoverRoute({ sectionId: section.id }));
    }
    return;
  };

  const navigatePreviousSectionCover = () => {
    const previousPageIndex = sectionData.value.pageNo - 2;
    if (previousPageIndex < getSectionList.value.length) {
      const previousSectionId = getSectionList.value[previousPageIndex].id;
      return router.push(getEditorSectionCoverRoute({ sectionId: previousSectionId }));
    }
    return;
  };

  const navigatePreviousSection = () => {
    //go to previous section
    const previousPageIndex = sectionData.value.pageNo - 2;
    if (previousPageIndex >= 0) {
      const previousSection = getSectionList.value[previousPageIndex];
      const activityListValue = getActivityList.value(previousSection.id);
      if (activityListValue.length > 0) {
        //go to last activity in previous section
        return goToByPageId(activityListValue[activityListValue.length - 1].id);
      } else {
        //go to previous section cover
        const previousSectionId = previousSection.id;
        return router.push(getEditorSectionCoverRoute({ sectionId: previousSectionId }));
      }
    }
    return;
  };

  const navigateNextActivity = (currentPage) => {
    if (currentPage.pageNo < activityList.value.length) {
      //go to next activity
      const nextPageIndex = currentPage.pageNo;
      if (nextPageIndex < activityList.value.length) {
        return goToByPageId(activityList.value[nextPageIndex].id);
      }
    } else if (currentPage.parentId !== null) {
      //go to next section cover
      const section = getSectionList.value.find((section) => {
        return section.id === currentPage.parentId;
      });
      if (section.pageNo < getSectionList.value.length) {
        const nextSectionId = getSectionList.value[section.pageNo].id;
        return router.push(getEditorSectionCoverRoute({ sectionId: nextSectionId }));
      }
    }
    return;
  };

  const navigateNextSection = () => {
    //go to first activity in section
    if (activityList.value.length !== 0) {
      return goToByPageId(activityList.value[0].id);
    } else {
      return navigateNextSectionCover();
    }
  };

  const navigateNextSectionCover = () => {
    //go to next section cover page
    const nextPageIndex = sectionData.value.pageNo;
    if (nextPageIndex < getSectionList.value.length) {
      const nextSectionId = getSectionList.value[nextPageIndex].id;
      return router.push(getEditorSectionCoverRoute({ sectionId: nextSectionId }));
    }
    return;
  };

  const goToCover = () => {
    return router.push(getEditorCoverRoute({}));
  };

  const goToSectionCover = (sectionId, showAuthoringCoPilot?) => {
    return router.push({
      name: "EditorSectionCover",
      params: processRouteParams({ sectionId: sectionId }),
      query: processRouteQuery({ showAuthoringCoPilot: showAuthoringCoPilot }),
    });
  };

  const goToByPageId = (pageId, showAuthoringCoPilot?) => {
    const activity = getActivity.value(pageId);
    const sectionId = `${activity.parentId}`;
    return router.push({
      name: "EditorActivityPage",
      params: processRouteParams({ sectionId, pageId: pageId }),
      query: processRouteQuery({
        pageId: pageId,
        showAuthoringCoPilot: showAuthoringCoPilot,
      }), // to retain route query params
    });
  };

  const goToByPageIdAndQuizPageNo = (pageId, pageNo) => {
    const activity = getActivity.value(pageId);
    const sectionId = `${activity.parentId}`;
    const query: LocationQueryRaw = { quizPage: pageNo };
    if (route?.query?.version) {
      query.version = route.query.version;
    }
    return router.push({
      name: "EditorActivityPage",
      params: processRouteParams({ sectionId, pageId: pageId }),
      query,
    });
  };

  const goToByPageIdAndPageNo = (pageId, pageNo) => {
    const activity = getActivity.value(pageId);
    const sectionId = `${activity.parentId}`;
    const query: LocationQueryRaw = { pageNo: pageNo };
    if (route?.query?.version) {
      query.version = route.query.version;
    }
    return router.push({
      name: "EditorActivityPage",
      params: processRouteParams({ sectionId, pageId: pageId }),
      query,
    });
  };

  const showAlpStageDisplayName = (item) => {
    let isQuizActivity = false;
    const components = getLeafComponentsInActivity.value(item.id.toString());
    components.every((component) => {
      if (component.type === LESSON_ACTIVITY_COMPONENT_TYPE.COMPONENT_QUIZ) {
        isQuizActivity = true;
        return false;
      }
      return true;
    });
    return (
      item &&
      !isQuizActivity &&
      item.alpStage &&
      item.alpStage.displayName &&
      item.alpStage.displayName !== "Custom"
    );
  };

  const alpStageDisplayName = (item) => {
    return item && item.alpStage && item.alpStage.displayName ? item.alpStage.displayName : "";
  };

  const confirmExit = (customRoute) => {
    if (customRoute === "classGroup") {
      const props = {
        title: t("lesson_exit_confirmation_title"),
        content: t("lesson_exit_confirmation", {
          destination: t("group_label"),
        }),
        primaryBtnLabel: t("button_edit_ok"),
        use_secondaryButton: t("button_edit_cancel"),
        actionPrimary: () => {
          store.dispatch("app/clearPageDirty");
          router.push({
            name: "StudentGroup",
            params: { collectionUuid: studentGroupCollectionUuid.value },
          });
        },
      };
      modal.showMessage("warning", props);
    } else if (customRoute === "dashboard") {
      const props = {
        title: t("lesson_exit_confirmation_title"),
        content: t("lesson_exit_confirmation", {
          destination: t("homepage_label"),
        }),
        primaryBtnLabel: t("button_edit_ok"),
        use_secondaryButton: t("button_edit_cancel"),
        actionPrimary: () => {
          store.dispatch("app/clearPageDirty");
          router.push({ name: "Dashboard" });
        },
      };
      modal.showMessage("warning", props);
    }
  };

  const navigateSectionPrevious = () => {
    // If the current section is the first section, navigate to the editor cover
    if (isFirstSection.value) {
      return router.push(getEditorCoverRoute({}));
    } else {
      // Go to the previous section
      const previousPageIndex = sectionData.value.pageNo - 2;
      if (previousPageIndex >= 0) {
        const previousSection = getSectionList.value[previousPageIndex];
        const activityListValue = getActivityList.value(previousSection.id);

        if (activityListValue.length > 0) {
          // Go to the last activity in the previous section
          const prevSectionLastActivity = activityListValue[activityListValue.length - 1];

          if (prevSectionLastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            quizNavigatorStoreRef.value.setCurrentPage(
              prevSectionLastActivity.id,
              QUIZ_PAGE.QUIZ_END,
            );
            return goToByPageIdAndQuizPageNo(prevSectionLastActivity.id, QUIZ_PAGE.QUIZ_END);
          } else if (prevSectionLastActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            if (isActivityHasPagination(prevSectionLastActivity.id)) {
              activityComponentStoreRef.value.setCurrentPageNo(
                prevSectionLastActivity.uuid,
                getActivityPageNo(prevSectionLastActivity.id),
              );
              return goToByPageIdAndPageNo(
                prevSectionLastActivity.id,
                getActivityPageNo(prevSectionLastActivity.id),
              );
            }
            return goToByPageId(prevSectionLastActivity.id);
          }
        } else {
          // If no activities, go to the previous section cover
          return router.push(getEditorSectionCoverRoute({ sectionId: previousSection.id }));
        }
      }
    }
    return;
  };

  const navigateSectionNext = () => {
    const activityListValue = getActivityList.value(currentSectionId.value);

    if (activityListValue?.length > 0) {
      // Get the first activity in the current section
      const firstActivity = activityListValue[0];
      if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        quizNavigatorStoreRef.value.setCurrentPage(firstActivity.id, QUIZ_PAGE.QUIZ_COVER);
        return goToByPageIdAndQuizPageNo(firstActivity.id, QUIZ_PAGE.QUIZ_COVER);
      } else if (firstActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
        if (isActivityHasPagination(firstActivity.id)) {
          activityComponentStoreRef.value.setCurrentPageNo(firstActivity.uuid, 1);
          return goToByPageIdAndPageNo(firstActivity.id, 1);
        }
        return goToByPageId(firstActivity.id);
      }
    } else {
      const nextPageIndex = sectionData.value.pageNo;
      if (nextPageIndex < getSectionList.value.length) {
        const nextSection = getSectionList.value[nextPageIndex];
        return router.push(getEditorSectionCoverRoute({ sectionId: nextSection.id }));
      }
    }
    return;
  };

  const getActivityDefaultLabel = (page) => {
    return isSectionActivityNumberHidden.value
      ? ""
      : t("bottom_navigation_default_label", {
          sectionLabel: StringUtils.getAlphabeticalLetter(sectionData.value.pageNo - 1),
          activityNo: page.pageNo,
        }).replace(/ /g, "\u00A0");
  };

  const getActivityQuizEndLabel = (page) => {
    return isSectionActivityNumberHidden.value
      ? t("bottom_navigation_quiz_end_with_no_section_label_or_activity_number_label")
      : t("bottom_navigation_quiz_end_label", {
          sectionLabel: StringUtils.getAlphabeticalLetter(sectionData.value.pageNo - 1),
          activityNo: page.pageNo,
        }).replace(/ /g, "\u00A0");
  };

  const getActivityQuizPageLabel = (page) => {
    const localizationKey = isSectionActivityNumberHidden.value
      ? "bottom_navigation_page_with_no_section_label_or_activity_number_label"
      : "bottom_navigation_page_label";

    return t(localizationKey, {
      sectionLabel: StringUtils.getAlphabeticalLetter(sectionData.value.pageNo - 1),
      activityNo: page.pageNo,
      currentPageNo: Number(currentPageNo.value) + 1,
    }).replace(/ /g, "\u00A0");
  };

  const getActivityPageLabel = (page) => {
    const localizationKey = isSectionActivityNumberHidden.value
      ? "bottom_navigation_page_with_no_section_label_or_activity_number_label"
      : "bottom_navigation_page_label";

    return t(localizationKey, {
      sectionLabel: StringUtils.getAlphabeticalLetter(sectionData.value.pageNo - 1),
      activityNo: page.pageNo,
      currentPageNo: currentPageNo.value,
    }).replace(/ /g, "\u00A0");
  };

  const getBottomNavigationSectionDefaultLabel = (sectionPageNo, sectionTitle) => {
    return isSectionActivityNumberHidden.value
      ? sectionTitle
      : t("bottom_navigation_section_default_label", {
          sectionLabel: StringUtils.getAlphabeticalLetter(sectionPageNo),
          sectionTitle: sectionTitle,
        });
  };

  const getBottomNavigationActivityQuizEndLabel = (activityNumber) => {
    return isSectionActivityNumberHidden.value
      ? t("bottom_navigation_quiz_end_with_no_section_label_or_activity_number_label")
      : t("bottom_navigation_activity_quiz_end_label", {
          activityNo: activityNumber,
        });
  };

  const getBottomNavigationActivityPageLabel = (
    activityNumber,
    activityTitle,
    currentPageNoParam,
  ) => {
    const localizationKey =
      isSectionActivityNumberHidden.value || DeviceUtils.isDesktopLarge()
        ? "bottom_navigation_activity_with_page_label_no_activity_number"
        : "bottom_navigation_activity_with_page_label";

    return t(localizationKey, {
      activityNo: activityNumber,
      activityTitle: activityTitle,
      currentPageNo: currentPageNoParam,
    }).replace(/ /g, "\u00A0");
  };

  const getBottomNavigationActivityLabel = (activityNumber, activityTitle) => {
    return isSectionActivityNumberHidden.value
      ? activityTitle
      : t("bottom_navigation_activity_label", {
          activityNo: activityNumber,
          activityTitle: activityTitle,
        });
  };

  const getActivityLabel = (currentPage) => {
    if (AssignmentUtils.isActivityOrQuizActivity(currentPage?.type)) {
      if (currentPage.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        if (currentPageNo.value === QUIZ_PAGE.QUIZ_COVER) {
          return getActivityDefaultLabel(currentPage);
        } else if (currentPageNo.value === QUIZ_PAGE.QUIZ_END) {
          return getActivityQuizEndLabel(currentPage);
        } else {
          return getActivityQuizPageLabel(currentPage);
        }
      } else {
        if (hasPagination.value) {
          return getActivityPageLabel(currentPage);
        }
        return getActivityDefaultLabel(currentPage);
      }
    }
    return;
  };

  const navigateActivityPrevious = (activity) => {
    if (!AssignmentUtils.isActivityOrQuizActivity(activity.type)) {
      return;
    }

    const currentActivityIndex = activity.pageNo - 1;

    if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
      if (currentPageNo.value === 1) {
        if (activity.pageNo === 1) {
          return router.push(getEditorSectionCoverRoute({ sectionId: sectionData.value.id }));
        }
        // Not first activity and on first page, go to previous activity
        const previousActivity = activityList.value[currentActivityIndex - 1];
        if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          quizNavigatorStoreRef.value.setCurrentPage(previousActivity.id, QUIZ_PAGE.QUIZ_END);
          return goToByPageIdAndQuizPageNo(previousActivity.id, QUIZ_PAGE.QUIZ_END);
        } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          if (isActivityHasPagination(previousActivity.id)) {
            activityComponentStoreRef.value.setCurrentPageNo(
              previousActivity.uuid,
              getActivityPageNo(previousActivity.id),
            );
            return goToByPageIdAndPageNo(
              previousActivity.id,
              getActivityPageNo(previousActivity.id),
            );
          }
          return goToByPageId(previousActivity.id);
        }
      } else if (hasPagination.value && Number(currentPageNo.value) > 0) {
        // Not first page and has pagination, go to previous page of same activity
        activityComponentStoreRef.value.setCurrentPageNo(
          activity.id,
          Number(currentPageNo.value) - 1,
        );
        return goToByPageIdAndPageNo(activity.id, Number(currentPageNo.value) - 1);
      }
    } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
      if (currentPageNo.value === QUIZ_PAGE.QUIZ_COVER) {
        if (activity.pageNo === 1) {
          return router.push(getEditorSectionCoverRoute({ sectionId: sectionData.value.id }));
        }
        // Not first quiz and on cover page, go to previous activity
        const previousActivity = activityList.value[currentActivityIndex - 1];
        if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
          quizNavigatorStoreRef.value.setCurrentPage(previousActivity.id, QUIZ_PAGE.QUIZ_END);
          return goToByPageIdAndQuizPageNo(previousActivity.id, QUIZ_PAGE.QUIZ_END);
        } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
          if (isActivityHasPagination(previousActivity.id)) {
            activityComponentStoreRef.value.setCurrentPageNo(
              previousActivity.uuid,
              getActivityPageNo(previousActivity.id),
            );
            return goToByPageIdAndPageNo(
              previousActivity.id,
              getActivityPageNo(previousActivity.id),
            );
          } else {
            return goToByPageId(previousActivity.id);
          }
        }
      } else if (
        currentPageNo.value !== QUIZ_PAGE.QUIZ_COVER &&
        currentPageNo.value !== QUIZ_PAGE.QUIZ_END &&
        currentPageNo.value === 0
      ) {
        // On first page, go to quiz cover
        quizNavigatorStoreRef.value.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_COVER);
        return goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_COVER);
      } else if (
        currentPageNo.value !== QUIZ_PAGE.QUIZ_COVER &&
        currentPageNo.value !== QUIZ_PAGE.QUIZ_END &&
        Number(currentPageNo.value) > 0
      ) {
        quizNavigatorStoreRef.value.setCurrentPage(activity.id, Number(currentPageNo.value) - 1);
        return goToByPageIdAndQuizPageNo(activity.id, Number(currentPageNo.value) - 1);
      } else if (currentPageNo.value === QUIZ_PAGE.QUIZ_END) {
        if (getActivityPageNo(activity.id) === 0) {
          quizNavigatorStoreRef.value.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_COVER);
          return goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_COVER);
        }
        quizNavigatorStoreRef.value.setCurrentPage(activity.id, getActivityPageNo(activity.id) - 1);
        return goToByPageIdAndQuizPageNo(activity.id, getActivityPageNo(activity.id) - 1);
      }
    }
    return;
  };

  const getActivityPreviousNavigationLabel = (activity) => {
    if (AssignmentUtils.isActivityOrQuizActivity(activity?.type)) {
      const currentActivityIndex = activity.pageNo - 1;

      if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
        if (currentPageNo.value === 1) {
          if (activity.pageNo === 1) {
            return getBottomNavigationSectionDefaultLabel(
              sectionData.value.pageNo - 1,
              sectionData.value.title,
            );
          }
          // Navigate to previous activity
          const previousActivity = activityList.value[currentActivityIndex - 1];

          if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            // previous activity is quiz
            return getBottomNavigationActivityQuizEndLabel(previousActivity.pageNo);
          } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            // Check if the previous activity has multiple pages
            if (isActivityHasPagination(previousActivity.id)) {
              // Navigate to the last page of the previous activity
              return getBottomNavigationActivityPageLabel(
                previousActivity.pageNo,
                previousActivity.title,
                getActivityPageNo(previousActivity.id),
              );
            } else {
              // The previous activity has no multiple pages
              return getBottomNavigationActivityLabel(
                previousActivity.pageNo,
                previousActivity.title,
              );
            }
          }
        } else if (hasPagination.value && Number(currentPageNo.value) > 1) {
          // Not first page and has pagination
          // Navigate to previous page of same activity
          return getBottomNavigationActivityPageLabel(
            activity.pageNo,
            activity.title,
            Number(currentPageNo.value) - 1,
          );
        } else {
          return getBottomNavigationActivityLabel(activity.pageNo, activity.title);
        }
      } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        if (currentPageNo.value === QUIZ_PAGE.QUIZ_COVER) {
          if (activity.pageNo === 1) {
            return getBottomNavigationSectionDefaultLabel(
              sectionData.value.pageNo - 1,
              sectionData.value.title,
            );
          }
          const previousActivity = activityList.value[currentActivityIndex - 1];

          if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            // previous activity is quiz
            return getBottomNavigationActivityQuizEndLabel(previousActivity.pageNo);
          } else if (previousActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            // Check if the previous activity has multiple pages
            if (isActivityHasPagination(previousActivity.id)) {
              // Navigate to the last page of the previous activity
              return getBottomNavigationActivityPageLabel(
                previousActivity.pageNo,
                previousActivity.title,
                getActivityPageNo(previousActivity.id),
              );
            } else {
              // The previous activity has no multiple pages
              return getBottomNavigationActivityLabel(
                previousActivity.pageNo,
                previousActivity.title,
              );
            }
          }
        } else if (
          currentPageNo.value !== QUIZ_PAGE.QUIZ_COVER &&
          currentPageNo.value !== QUIZ_PAGE.QUIZ_END &&
          currentPageNo.value === 0
        ) {
          return getBottomNavigationActivityLabel(activity.pageNo, activity.title);
        } else if (
          currentPageNo.value !== QUIZ_PAGE.QUIZ_COVER &&
          currentPageNo.value !== QUIZ_PAGE.QUIZ_END &&
          Number(currentPageNo.value) > 0
        ) {
          return getBottomNavigationActivityPageLabel(
            activity.pageNo,
            activity.title,
            currentPageNo.value,
          );
        } else if (currentPageNo.value === QUIZ_PAGE.QUIZ_END) {
          if (getActivityPageNo(activity.id) === 0) {
            return getBottomNavigationActivityLabel(activity.pageNo, activity.title);
          }
          return getBottomNavigationActivityPageLabel(
            activity.pageNo,
            activity.title,
            getActivityPageNo(activity.id),
          );
        }
      }
    }
  };

  const navigateActivityNext = (activity) => {
    if (!AssignmentUtils.isActivityOrQuizActivity(activity.type)) {
      return;
    }
    const currentActivityIndex = activity.pageNo - 1;

    if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
      if (hasPagination.value) {
        if (currentPageNo.value < getActivityPageNo(activity.id)) {
          // Not on last page, go to next page of same activity
          activityComponentStoreRef.value.setCurrentPageNo(
            activity.uuid,
            Number(currentPageNo.value) + 1,
          );
          return goToByPageIdAndPageNo(activity.id, Number(currentPageNo.value) + 1);
        } else {
          // On last page, check for next activity or section
          if (activity.pageNo === totalNumActivities.value) {
            // Last activity in section, go to next section
            if (!isLastSection.value) {
              return router.push(
                getEditorSectionCoverRoute({ sectionId: nextSectionData.value.id }),
              );
            }
          } else {
            // Go to next activity
            const nextActivity = activityList.value[currentActivityIndex + 1];
            if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              quizNavigatorStoreRef.value.setCurrentPage(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
              return goToByPageIdAndQuizPageNo(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
            } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              if (isActivityHasPagination(nextActivity.id)) {
                activityComponentStoreRef.value.setCurrentPageNo(nextActivity.uuid, 1);
                return goToByPageIdAndPageNo(nextActivity.id, 1);
              } else {
                return goToByPageId(nextActivity.id);
              }
            }
          }
        }
      } else {
        // No pagination, go to next activity or section
        if (activity.pageNo === totalNumActivities.value) {
          // Last activity in section, go to next section
          if (!isLastSection.value) {
            return router.push(getEditorSectionCoverRoute({ sectionId: nextSectionData.value.id }));
          }
        } else {
          // Go to next activity
          const nextActivity = activityList.value[currentActivityIndex + 1];
          if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            quizNavigatorStoreRef.value.setCurrentPage(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
            return goToByPageIdAndQuizPageNo(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
          } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            if (isActivityHasPagination(nextActivity.id)) {
              activityComponentStoreRef.value.setCurrentPageNo(nextActivity.uuid, 1);
              return goToByPageIdAndPageNo(nextActivity.id, 1);
            }
            return goToByPageId(nextActivity.id);
          }
        }
      }
    } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
      if (currentPageNo.value === QUIZ_PAGE.QUIZ_COVER) {
        if (getActivityPageNo(activity.id) === 0) {
          quizNavigatorStoreRef.value.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_END);
          return goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_END);
        }
        // On cover page, go to first quiz question
        quizNavigatorStoreRef.value.setCurrentPage(activity.id, 0);
        return goToByPageIdAndQuizPageNo(activity.id, 0);
      } else if (currentPageNo.value === QUIZ_PAGE.QUIZ_END) {
        // On end page, check for next activity or section
        if (activity.pageNo === totalNumActivities.value) {
          // Last activity in section, go to next section
          if (!isLastSection.value) {
            return router.push(getEditorSectionCoverRoute({ sectionId: nextSectionData.value.id }));
          }
        } else {
          // Go to next activity
          const nextActivity = activityList.value[currentActivityIndex + 1];
          if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
            quizNavigatorStoreRef.value.setCurrentPage(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
            return goToByPageIdAndQuizPageNo(nextActivity.id, QUIZ_PAGE.QUIZ_COVER);
          } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
            if (isActivityHasPagination(nextActivity.id)) {
              activityComponentStoreRef.value.setCurrentPageNo(nextActivity.uuid, 1);
              return goToByPageIdAndPageNo(nextActivity.id, 1);
            }
            return goToByPageId(nextActivity.id);
          }
        }
      } else if (Number(currentPageNo.value) < getActivityPageNo(activity.id) - 1) {
        // Not on last question, go to next question
        quizNavigatorStoreRef.value.setCurrentPage(activity.id, Number(currentPageNo.value) + 1);
        return goToByPageIdAndQuizPageNo(activity.id, Number(currentPageNo.value) + 1);
      } else {
        // On last question, go to end page
        quizNavigatorStoreRef.value.setCurrentPage(activity.id, QUIZ_PAGE.QUIZ_END);
        return goToByPageIdAndQuizPageNo(activity.id, QUIZ_PAGE.QUIZ_END);
      }
    }
    return;
  };

  const getActivityNextNavigationLabel = (activity) => {
    if (AssignmentUtils.isActivityOrQuizActivity(activity?.type)) {
      const currentActivityIndex = activity.pageNo - 1;

      if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
        if (hasPagination.value) {
          if (currentPageNo.value < getActivityPageNo(activity.id)) {
            // Not on last page, go to next page of same activity
            return getBottomNavigationActivityPageLabel(
              activity.pageNo,
              activity.title,
              Number(currentPageNo.value) + 1,
            );
          } else {
            // On last page, check for next activity or section
            if (activity.pageNo === totalNumActivities.value) {
              // Last activity in section, go to next section
              if (!isLastSection.value) {
                return getBottomNavigationSectionDefaultLabel(
                  nextSectionData.value.pageNo - 1,
                  nextSectionData.value.title,
                );
              }
            } else {
              // Go to next activity
              const nextActivity = activityList.value[currentActivityIndex + 1];
              if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
                return getBottomNavigationActivityLabel(nextActivity.pageNo, nextActivity.title);
              } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
                if (isActivityHasPagination(nextActivity.id)) {
                  return getBottomNavigationActivityPageLabel(
                    nextActivity.pageNo,
                    nextActivity.title,
                    1,
                  );
                } else {
                  return getBottomNavigationActivityLabel(nextActivity.pageNo, nextActivity.title);
                }
              }
            }
          }
        } else {
          // No pagination, go to next activity or section
          if (activity.pageNo === totalNumActivities.value) {
            // Last activity in section, go to next section
            if (!isLastSection.value) {
              return getBottomNavigationSectionDefaultLabel(
                nextSectionData.value.pageNo - 1,
                nextSectionData.value.title,
              );
            }
          } else {
            // Go to next activity
            const nextActivity = activityList.value[currentActivityIndex + 1];
            if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              return getBottomNavigationActivityLabel(nextActivity.pageNo, nextActivity.title);
            } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              if (isActivityHasPagination(nextActivity.id)) {
                return getBottomNavigationActivityPageLabel(
                  nextActivity.pageNo,
                  nextActivity.title,
                  1,
                );
              } else {
                return getBottomNavigationActivityLabel(nextActivity.pageNo, nextActivity.title);
              }
            }
          }
        }
      } else if (activity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
        if (currentPageNo.value === QUIZ_PAGE.QUIZ_COVER) {
          if (getActivityPageNo(activity.id) === 0) {
            return getBottomNavigationActivityQuizEndLabel(activity.pageNo);
          }
          return getBottomNavigationActivityPageLabel(activity.pageNo, activity.title, 1);
        } else if (currentPageNo.value === QUIZ_PAGE.QUIZ_END) {
          // On end page, check for next activity or section
          if (activity.pageNo === totalNumActivities.value) {
            // Last activity in section, go to next section
            if (!isLastSection.value) {
              return getBottomNavigationSectionDefaultLabel(
                nextSectionData.value.pageNo - 1,
                nextSectionData.value.title,
              );
            }
          } else {
            // Go to next activity
            const nextActivity = activityList.value[currentActivityIndex + 1];
            if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ) {
              return getBottomNavigationActivityLabel(nextActivity.pageNo, nextActivity.title);
            } else if (nextActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY) {
              if (isActivityHasPagination(nextActivity.id)) {
                return getBottomNavigationActivityPageLabel(
                  nextActivity.pageNo,
                  nextActivity.title,
                  1,
                );
              } else {
                return getBottomNavigationActivityLabel(nextActivity.pageNo, nextActivity.title);
              }
            }
          }
        } else if (Number(currentPageNo.value) < getActivityPageNo(activity.id) - 1) {
          return getBottomNavigationActivityPageLabel(
            activity.pageNo,
            activity.title,
            Number(currentPageNo.value) + 2,
          );
        } else {
          return t("bottom_navigation_quiz_end_label", {
            activityNumber: activity.pageNo,
          });
        }
      }
    }
  };

  // Export all properties and methods
  return {
    // Data
    quizNavigatorStore: quizNavigatorStoreRef,
    activityComponentStore: activityComponentStoreRef,

    // Computed
    lesson,
    isCoursePackageType,
    currentPageId,
    currentSectionId,
    currentSection,
    hideSectionActivityNumber,
    getActivity,
    getActivityList,
    resourceDomain,
    isAdminPage,
    targetLessonStatus,
    getSectionList,
    getLeafComponentsInActivity,
    showSectionCover,
    getComponentsByContainerId,
    currentMode,
    isSectionActivityNumberHidden,
    activityList,
    sectionData,
    nextSectionData,
    fromCover,
    fromSectionCover,
    fromActivity,
    fromModulePlan,
    fromModulePlanReorder,
    isActiveSectionCoverPage,
    isActiveActivityPage,
    isFirstSection,
    isLastSection,
    sectionLabel,
    nextSectionLabel,
    hasSections,
    hasActivities,
    activitiesInFirstSection,
    previousNavigationSectionLabel,
    nextNavigationSectionLabel,
    previousSectionLastActivityLabel,

    // Methods
    getActivityPageNo,
    isActivityHasPagination,
    processRouteQuery,
    processRouteParams,
    getEditorPageRoute,
    routeToEditorPage,
    getEditorCoverRoute,
    routeToEditorCover,
    getEditorEndRoute,
    getEditorModulePlanRoute,
    getEditorSectionCoverRoute,
    routeToEditorSectionCover,
    routeToNotFoundPage,
    getAssignmentViewRouteByCurrentPageId,
    getRouteByCurrentPageId,
    handlePrevious,
    handleNext,
    navigateLessonCoverNext,
    navigateLessonEndPrevious,
    navigatePreviousActivity,
    navigatePreviousSectionCover,
    navigatePreviousSection,
    navigateNextActivity,
    navigateNextSection,
    navigateNextSectionCover,
    goToCover,
    goToSectionCover,
    goToByPageId,
    goToByPageIdAndQuizPageNo,
    goToByPageIdAndPageNo,
    showAlpStageDisplayName,
    alpStageDisplayName,
    confirmExit,
    navigateSectionPrevious,
    navigateSectionNext,
    getActivityDefaultLabel,
    getActivityQuizEndLabel,
    getActivityQuizPageLabel,
    getActivityPageLabel,
    getBottomNavigationSectionDefaultLabel,
    getBottomNavigationActivityQuizEndLabel,
    getBottomNavigationActivityPageLabel,
    getBottomNavigationActivityLabel,
    getActivityLabel,
    navigateActivityPrevious,
    getActivityPreviousNavigationLabel,
    navigateActivityNext,
    getActivityNextNavigationLabel,
    getEditorModulePlanReorderRoute,
    getEditorModulePlanLearningAssistantSettingsRoute,
  };
}
